// hooks/useIdVerification.ts
//
// Hook for KYC ID verification

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IdVerificationService,
  type VerificationMethod,
} from "@/services/kyc/idVerificationService";
import { toast } from "sonner";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useAuth } from "./useAuth";
import { UserType } from "@/types/user";
import { authLabels } from "@/i18n";
import { getLanguage } from "@/utils/languageUtils";

const KYC_STATUS_QUERY_KEY = ["kyc", "status"];
const POLLING_INTERVAL = 30000;
const POLLING_INTERVAL_DIALOG = 3000;

/** Shared across hook instances — one React Query poller per cache key. */
let verificationStartedGlobal = false;
let kycFastPollActive = false;
/**
 * Shared announcement state so Dashboard + banners don't each toast on login.
 * `undefined` = not hydrated yet from a real API payload.
 */
let hydratedEffectiveStatus: string | null | undefined = undefined;
let hydratedBankIdStatus: string | null | undefined = undefined;
let successDialogOwner: ((open: boolean) => void) | null = null;

type StatusPayload = {
  kycStatus: string | null;
  bankIdStatus: string | null;
  bankIdFailureReason: string | null;
  hasPersonalIdentityNumber: boolean;
};

function isPendingPayload(status: StatusPayload | undefined): boolean {
  if (!status) return false;
  return (
    status.kycStatus === "pending" ||
    status.kycStatus === "in_review" ||
    status.bankIdStatus === "pending"
  );
}

function getEffectiveStatus(
  status: StatusPayload | undefined,
): string | null {
  if (!status) return null;
  if (status.bankIdStatus === "approved" || status.kycStatus === "approved") {
    return "approved";
  }
  if (
    status.bankIdStatus === "pending" ||
    status.kycStatus === "pending" ||
    status.kycStatus === "in_review"
  ) {
    return "pending";
  }
  if (
    status.bankIdStatus === "declined" &&
    status.kycStatus === "declined"
  ) {
    return "declined";
  }
  return null;
}

function shouldAnnounceTransition(
  previous: string | null | undefined,
  next: string | null,
): boolean {
  // Never announce the first hydrate (e.g. already-approved user logging in).
  if (previous === undefined) return false;
  if (previous === next) return false;
  // Only celebrate real verification outcomes after a pending/unknown flow.
  if (next === "approved" || next === "declined") {
    return previous === "pending" || previous === null || verificationStartedGlobal;
  }
  return false;
}

export const useIdVerification = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const verificationStartedRef = useRef(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const labels = useMemo(() => authLabels(getLanguage()), []);

  const isAdmin = user?.type === UserType.Admin;
  const shouldFetchKyc = !!user?.id && !isAdmin;

  // Register one owner for the success dialog (Dashboard typically).
  useEffect(() => {
    successDialogOwner = setShowSuccessDialog;
    return () => {
      if (successDialogOwner === setShowSuccessDialog) {
        successDialogOwner = null;
      }
    };
  }, []);

  // Reset shared hydrate markers when logged-out so next login re-hydrates cleanly
  // without treating "already approved" as a fresh approval.
  useEffect(() => {
    if (!user?.id) {
      hydratedEffectiveStatus = undefined;
      hydratedBankIdStatus = undefined;
      verificationStartedGlobal = false;
      kycFastPollActive = false;
    }
  }, [user?.id]);

  const {
    data: statusData,
    isLoading: isLoadingStatus,
    refetch: refetchStatus,
  } = useQuery({
    queryKey: KYC_STATUS_QUERY_KEY,
    queryFn: async (): Promise<StatusPayload> => {
      const response = await IdVerificationService.getStatus();
      return {
        kycStatus: response.kycStatus,
        bankIdStatus: response.bankIdStatus,
        bankIdFailureReason: response.bankIdFailureReason,
        hasPersonalIdentityNumber: Boolean(response.hasPersonalIdentityNumber),
      };
    },
    enabled: shouldFetchKyc,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      if (!shouldFetchKyc) return false;
      if (kycFastPollActive) return POLLING_INTERVAL_DIALOG;
      const status = query.state.data as StatusPayload | undefined;
      if (isPendingPayload(status) || verificationStartedGlobal) {
        return POLLING_INTERVAL;
      }
      return false;
    },
  });

  const sessionUrlRef = useRef<{
    method: VerificationMethod;
    url: string;
  } | null>(null);

  const openUrlInNewTab = useCallback((url: string) => {
    void (async () => {
      const { isNativeCapacitorApp } = await import(
        "@/integrations/clients/carAuction/nativeAuth"
      );
      if (isNativeCapacitorApp()) {
        const { openBankIdInSystemBrowser } = await import(
          "@/lib/native/bankIdBrowser"
        );
        await openBankIdInSystemBrowser(url);
        // iOS resolves when the auth sheet closes; fetch the outcome right away.
        void queryClient.invalidateQueries({ queryKey: KYC_STATUS_QUERY_KEY });
        return;
      }
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
    })();
  }, [queryClient]);

  const startMutation = useMutation({
    mutationFn: (method: VerificationMethod) =>
      IdVerificationService.start(method),
    onSuccess: (data, method) => {
      if (!data || !data.sessionUrl) {
        toast.error(labels.kyc?.toast?.startFailed, {
          description: labels.kyc?.toast?.invalidResponse,
        });
        return;
      }

      verificationStartedRef.current = true;
      verificationStartedGlobal = true;
      sessionUrlRef.current = { method, url: data.sessionUrl };
      openUrlInNewTab(data.sessionUrl);
      queryClient.invalidateQueries({ queryKey: KYC_STATUS_QUERY_KEY });
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: KYC_STATUS_QUERY_KEY });
      }, 2000);
    },
    onError: (error: unknown) => {
      console.error("KYC start error:", error);
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        labels.kyc?.toast?.startFailed;
      toast.error(labels.kyc?.toast?.startFailed, {
        description: errorMessage,
      });
    },
  });

  useEffect(() => {
    const kycStatus = statusData?.kycStatus;
    const bankIdStatus = statusData?.bankIdStatus;
    const isFinalApproved =
      kycStatus === "approved" || bankIdStatus === "approved";
    const stillPending =
      kycStatus === "pending" ||
      kycStatus === "in_review" ||
      bankIdStatus === "pending";
    const isFinalDeclined =
      !isFinalApproved &&
      !stillPending &&
      (kycStatus === "declined" || bankIdStatus === "declined");

    if (isFinalApproved || isFinalDeclined) {
      verificationStartedRef.current = false;
      // Keep verificationStartedGlobal true until after announce effect runs once,
      // then clear below when announcing — or clear here only if already hydrated.
      if (hydratedEffectiveStatus !== undefined) {
        verificationStartedGlobal = false;
      }
    }
  }, [statusData]);

  // Announce KYC/BankID effective status changes once across all hook instances.
  useEffect(() => {
    if (!statusData || isAdmin) return;

    const effectiveStatus = getEffectiveStatus(statusData);
    const previous = hydratedEffectiveStatus;

    if (previous === undefined) {
      // First real payload after login — baseline only, never toast.
      hydratedEffectiveStatus = effectiveStatus;
      return;
    }

    if (!shouldAnnounceTransition(previous, effectiveStatus)) {
      hydratedEffectiveStatus = effectiveStatus;
      return;
    }

    hydratedEffectiveStatus = effectiveStatus;
    queryClient.invalidateQueries({ queryKey: ["userDetails"] });

    if (effectiveStatus === "approved") {
      verificationStartedGlobal = false;
      successDialogOwner?.(true);
      toast.success(labels.kyc?.toast?.verified, {
        id: "kyc-verified",
        description: labels.kyc?.toast?.verifiedDescription,
      });
      window.setTimeout(() => {
        successDialogOwner?.(false);
      }, 3000);
    } else if (effectiveStatus === "declined") {
      verificationStartedGlobal = false;
      toast.error(labels.kyc?.toast?.declined, {
        id: "kyc-declined",
        description: labels.kyc?.toast?.declinedDescription,
      });
    }
  }, [statusData, isAdmin, labels, queryClient]);

  const startVerification = useCallback(
    (method: VerificationMethod, options?: { forceRestart?: boolean }) => {
      if (isAdmin) return;

      if (options?.forceRestart) {
        sessionUrlRef.current = null;
        verificationStartedRef.current = true;
        verificationStartedGlobal = true;
      }

      if (sessionUrlRef.current?.method === method) {
        openUrlInNewTab(sessionUrlRef.current.url);
        return;
      }

      startMutation.mutate(method);
    },
    [isAdmin, openUrlInNewTab, startMutation],
  );

  const checkStatus = () => {
    if (isAdmin) return;
    queryClient.invalidateQueries({ queryKey: KYC_STATUS_QUERY_KEY });
    refetchStatus();
  };

  const kycStatus = statusData?.kycStatus ?? null;
  const bankIdStatus = statusData?.bankIdStatus ?? null;
  const bankIdFailureReason = statusData?.bankIdFailureReason ?? null;
  const hasPersonalIdentityNumber = Boolean(statusData?.hasPersonalIdentityNumber);
  const isApproved = isAdmin
    ? true
    : kycStatus === "approved" || bankIdStatus === "approved";
  const isPending = isAdmin
    ? false
    : kycStatus === "pending" ||
      kycStatus === "in_review" ||
      bankIdStatus === "pending";
  const isDeclined = isAdmin
    ? false
    : !isApproved &&
      !isPending &&
      (kycStatus === "declined" || bankIdStatus === "declined");
  const needsVerification = isAdmin ? false : !isApproved;

  // Complement BankID: only toast when BankID moves out of pending after Didit is approved.
  useEffect(() => {
    if (isAdmin || isLoadingStatus || !statusData) return;

    const previous = hydratedBankIdStatus;
    if (previous === undefined) {
      hydratedBankIdStatus = bankIdStatus;
      return;
    }

    if (previous === bankIdStatus) return;

    const wasPending = previous === "pending" || verificationStartedGlobal;
    hydratedBankIdStatus = bankIdStatus;
    queryClient.invalidateQueries({ queryKey: ["userDetails"] });

    if (!wasPending || kycStatus !== "approved") return;

    // Skip if the generic effective-status announcer already covers this approval.
    if (bankIdStatus === "approved" && previous === "pending") {
      // Already handled by effective-status effect when Didit was pending too;
      // only toast here when Didit was already approved (complement flow).
      if (hydratedEffectiveStatus === "approved") {
        toast.success(labels.kyc?.toast?.verified, {
          id: "kyc-verified",
          description: labels.kyc?.toast?.verifiedDescription,
        });
      }
    } else if (bankIdStatus === "declined") {
      toast.error(labels.kyc?.toast?.declined, {
        id: "kyc-declined",
        description: labels.kyc?.toast?.declinedDescription,
      });
    }
  }, [
    bankIdStatus,
    kycStatus,
    isAdmin,
    isLoadingStatus,
    statusData,
    queryClient,
    labels,
  ]);

  const startFastPolling = useCallback(() => {
    if (isAdmin || !user?.id) return;
    kycFastPollActive = true;
    queryClient.invalidateQueries({ queryKey: KYC_STATUS_QUERY_KEY });
  }, [user?.id, isAdmin, queryClient]);

  const stopFastPolling = useCallback(() => {
    kycFastPollActive = false;
    queryClient.invalidateQueries({ queryKey: KYC_STATUS_QUERY_KEY });
  }, [queryClient]);

  return {
    kycStatus: isAdmin ? null : kycStatus,
    bankIdStatus: isAdmin ? null : bankIdStatus,
    bankIdFailureReason: isAdmin ? null : bankIdFailureReason,
    hasPersonalIdentityNumber: isAdmin ? true : hasPersonalIdentityNumber,
    isLoadingStatus: isAdmin ? false : isLoadingStatus,
    isApproved,
    isPending,
    isDeclined,
    needsVerification,
    startVerification,
    checkStatus,
    isStarting: startMutation.isPending,
    hasStartedVerification: verificationStartedRef.current,
    showSuccessDialog,
    setShowSuccessDialog,
    startFastPolling,
    stopFastPolling,
  };
};
